import SEOMeta from '@/components/base/SEOMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import CategoryTags from './components/CategoryTags';
import FeaturedComics from './components/FeaturedComics';
import RankingAndLatest from './components/RankingAndLatest';
import NewsletterSection from './components/NewsletterSection';

const baseUrl = 'https://hanmandq.com';

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '韩漫大全',
  url: baseUrl,
  description: '韩漫大全免费平台是韩国漫画在线阅读网标杆，每日更新最新章节，提供海量韩漫资源，支持评论与收藏功能，让您随时随地畅享韩漫阅读乐趣。',
  potentialAction: {
    '@type': 'SearchAction',
    target: `${baseUrl}/search?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '韩漫大全',
  url: baseUrl,
  description: '韩国漫画在线阅读网标杆平台',
  sameAs: [
    'https://weibo.com/hanmandq',
    'https://weixin.qq.com/hanmandq',
  ],
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, item: { '@id': baseUrl, name: '韩漫大全首页' } },
  ],
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '韩漫大全是什么？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '韩漫大全是韩国漫画在线阅读标杆平台，提供海量韩漫资源，每日更新最新章节，支持评论与收藏功能，让读者随时随地畅享韩漫阅读乐趣。',
      },
    },
    {
      '@type': 'Question',
      name: '韩漫大全是否免费？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '是的，韩漫大全平台提供免费韩漫阅读服务，用户无需付费即可浏览海量韩国漫画资源。',
      },
    },
    {
      '@type': 'Question',
      name: '韩漫大全每日更新多少部漫画？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '韩漫大全平台每日更新多部热门漫画的最新章节，涵盖恋爱、奇幻、动作、悬疑等多个分类，确保读者不会错过任何精彩内容。',
      },
    },
    {
      '@type': 'Question',
      name: '如何在韩漫大全收藏喜欢的漫画？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '在韩漫大全平台上，读者可以通过收藏功能标记自己喜欢的漫画作品，方便日后快速找到并继续阅读。',
      },
    },
  ],
};

export default function Home() {
  return (
    <>
      <SEOMeta
        title="韩漫大全免费-韩国漫画在线阅读网 | 每日更新韩漫资源"
        description="韩漫大全免费平台是韩国漫画在线阅读网标杆，每日更新最新章节，提供海量韩漫资源，支持评论与收藏功能，让您随时随地畅享韩漫阅读乐趣。涵盖恋爱、奇幻、动作、悬疑、搞笑等多种分类。"
        keywords="韩漫大全免费,韩国漫画,在线阅读网,每日更新,评论收藏,韩漫在线,免费漫画"
        canonicalUrl={baseUrl}
        ogImage="https://readdy.ai/api/search-image?query=Korean%20manhwa%20reading%20website%20banner%2C%20beautiful%20illustration%20collage%20of%20various%20Korean%20comic%20styles%2C%20warm%20and%20inviting%20atmosphere%2C%20clean%20design%2C%20text%20space%20for%20title%2C%20professional%20web%20banner&width=1200&height=630&seq=hanman-home-og&orientation=landscape"
        schemaData={[websiteSchema, organizationSchema, breadcrumbSchema, faqSchema]}
      />

      <Navbar />

      <main>
        <section className="relative w-full h-[420px] md:h-[560px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20warm%20and%20artistic%20Korean%20manhwa%20style%20illustration%20background%20with%20soft%20gradient%20colors%2C%20abstract%20comic%20page%20elements%20floating%20gently%2C%20warm%20peach%20and%20cream%20tones%2C%20dreamy%20reading%20atmosphere%2C%20clean%20and%20minimal%20composition%2C%20subtle%20line%20art%20textures%2C%20peaceful%20and%20inviting%20mood&width=1600&height=900&seq=hanman-hero-bg&orientation=landscape"
            alt="韩漫大全 - 韩国漫画在线阅读平台"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>

          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-5xl font-heading text-background-50 mb-4 leading-tight">
              韩漫大全 - 韩国漫画在线阅读网
            </h1>
            <p className="text-base md:text-lg text-background-100/90 mb-2 max-w-2xl mx-auto leading-relaxed">
              韩漫大全免费平台是韩国漫画在线阅读网标杆，每日更新最新章节
            </p>
            <p className="text-sm md:text-base text-background-100/80 mb-8 max-w-lg mx-auto">
              提供海量韩漫资源，支持评论与收藏功能，让您随时随地畅享韩漫阅读乐趣
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="#categories"
                className="px-8 py-3 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-medium text-sm transition-all whitespace-nowrap"
              >
                开始阅读
              </a>
              <a
                href="#featured"
                className="px-8 py-3 rounded-full bg-background-50/20 hover:bg-background-50/30 text-background-50 border border-background-50/30 font-medium text-sm transition-all whitespace-nowrap"
              >
                热门推荐
              </a>
            </div>

            <div className="flex items-center justify-center gap-6 mt-10">
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-accent-400">10,000+</p>
                <p className="text-xs text-background-100/70 mt-1">漫画资源</p>
              </div>
              <div className="w-px h-10 bg-background-50/20"></div>
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-accent-400">500万+</p>
                <p className="text-xs text-background-100/70 mt-1">活跃读者</p>
              </div>
              <div className="w-px h-10 bg-background-50/20"></div>
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-accent-400">每日更新</p>
                <p className="text-xs text-background-100/70 mt-1">从不间断</p>
              </div>
            </div>
          </div>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <i className="ri-arrow-down-line text-background-50/60 text-2xl"></i>
          </div>
        </section>

        <div id="categories">
          <CategoryTags />
        </div>

        <div id="featured">
          <FeaturedComics />
        </div>

        <RankingAndLatest />

        <NewsletterSection />
      </main>

      <Footer />
    </>
  );
}